import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "react-loading-indicator";
import { useInView } from "react-intersection-observer";

import AdAssistantStore from "../../AdAssistantStore";
import { withRefluxStore } from "../../../../Stores";
import {
  ActionsRow,
  VersionCard,
  VersionChanges,
  VersionMeta,
  VersionPanel,
  VersionCardSeparator,
  VersionCardWrapper,
  SpinnerContainer,
  EmptyCard
} from "./VersionHistory.styled";
import { ActionsAdAssistant } from "../../../../Actions/ActionsAdAssistant/ActionsAdAssistant";
import ContinueButton from "../../../../Components/ContinueButton/ContinueButton";
import VersionNavigation from "../version-navigation/VersionNavigation";

const VersionHistory = ({
  adId,
  // Store
  adVersions,
  currentAd,
  isLoadingVersions,
  isLoadingMoreVersions,
  isLoadingActiveAdVersion,
  hasMoreVersions
}) => {
  const { t } = useTranslation();

  const loadMore = useCallback(
    (inView) => {
      if (inView && !isLoadingMoreVersions && hasMoreVersions) {
        ActionsAdAssistant.loadMoreVersions();
      }
    },
    [isLoadingMoreVersions, hasMoreVersions]
  );

  const { ref: loadMoreRef } = useInView({
    threshold: 0.1,
    onChange: loadMore
  });

  const handleSelectVersion = (version) => {
    const adVersion = adVersions.find((v) => v.id === version.id);
    if (adVersion) {
      ActionsAdAssistant.setCurrentAdVersion(adVersion);
    }
  };

  const handleSelectActiveVersion = () => {
    ActionsAdAssistant.setActiveAdVersion(currentAd.id, currentAd.versionId);
  };

  if (isLoadingVersions) {
    return (
      <VersionPanel>
        <SpinnerContainer>
          <LoadingIndicator />
        </SpinnerContainer>
      </VersionPanel>
    );
  }

  return (
    <div>
      <VersionPanel>
        {adVersions?.length > 0 ? (
          adVersions.map((adVersion, index) => {
            const date = new Date(adVersion.updatedDate);
            const formattedDate = date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric"
            });
            const formattedTime = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit"
            });
            return (
              <VersionCardWrapper key={adVersion.id}>
                <VersionCard
                  selected={adVersion.version === currentAd.version}
                  onClick={() => handleSelectVersion(adVersion)}
                >
                  <VersionMeta>
                    <span>{formattedDate}</span>
                    {` ${t("at")} `}
                    <span>{formattedTime}</span>
                    {` ${t("by")} `}
                    <span>{adVersion.updatedByUserName}</span>
                  </VersionMeta>
                  <VersionChanges>
                    {adVersion?.changes?.length > 0
                      ? t("change", { count: adVersion.changes.length })
                      : t("No changes tracked")}
                  </VersionChanges>
                </VersionCard>
                {index < adVersions.length - 1 && <VersionCardSeparator />}
              </VersionCardWrapper>
            );
          })
        ) : (
          <EmptyCard>
            <VersionMeta>{t("No recent changes made")}</VersionMeta>
            <VersionCardSeparator />
          </EmptyCard>
        )}
        {/* Dedicated loading element that we observe */}
        <div
          ref={loadMoreRef}
          style={{
            height: "50px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          {isLoadingMoreVersions && (
            <SpinnerContainer>
              <LoadingIndicator />
            </SpinnerContainer>
          )}
        </div>
      </VersionPanel>
      <ActionsRow>
        <VersionNavigation />
        <ContinueButton
          height={36}
          onClick={handleSelectActiveVersion}
          isDisabled={isLoadingActiveAdVersion}
          isLoading={isLoadingActiveAdVersion}
        >
          {t("Done")}
        </ContinueButton>
      </ActionsRow>
    </div>
  );
};

export default withRefluxStore(
  VersionHistory,
  [AdAssistantStore],
  [
    "adVersions",
    "currentAdVersion",
    "currentAd",
    "isLoadingVersions",
    "isLoadingMoreVersions",
    "isLoadingActiveAdVersion",
    "hasMoreVersions"
  ]
);
